import React, { useState, useEffect, useCallback } from 'react';

interface CountdownButtonProps {
  onSend: () => Promise<void> | void;
  /**
   * 流程就绪态（手机号合法且邀请码条件满足）：
   * true → 黑底白字可点；false → rgba(20,20,20,0.25) 灰底禁用
   * 对应新稿 121:28 / 121:147 验证码框内嵌按钮形态
   */
  active?: boolean;
  className?: string;
}

/**
 * 验证码框内嵌「获取验证码」按钮 —— 新稿内嵌形态：
 * 94x32 / 圆角 8px / 文字 12px，绝对定位于验证码输入框右侧
 * 倒计时 60s，发送期间防重复点击
 */
export const CountdownButton: React.FC<CountdownButtonProps> = ({
  onSend,
  active = false,
  className = '',
}) => {
  const [countdown, setCountdown] = useState(0);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleClick = useCallback(async () => {
    if (countdown > 0 || !active || isSending) return;
    setIsSending(true);
    try {
      await onSend();
      setCountdown(60);
    } finally {
      setIsSending(false);
    }
  }, [countdown, active, isSending, onSend]);

  const isDisabled = countdown > 0 || !active || isSending;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={`absolute right-2 top-1/2 -translate-y-1/2 flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg transition-colors ${className}`}
      style={{
        width: '94px',
        height: '32px',
        backgroundColor: active ? '#141414' : 'rgba(20, 20, 20, 0.25)',
        color: '#FFFFFF',
        fontSize: '12px',
        lineHeight: '12px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
    >
      {isSending ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
    </button>
  );
};
